import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { 
  AddInstructor, 
  AddWorkout, 
  AddWorkoutType, 
  EditWorkout, 
  getAllWorkouts, 
  GetAllWorkoutsSecured, 
  GetInstructorList, 
  getParticipantEmail, 
  GetSingleWorkout, 
  GetSingleWorkoutSecured, 
  GetWorkoutsTypesList, 
  RemoveInstructor, 
  RemoveParticipant, 
  RemoveWorkoutType, 
  selfRemoveParticipant, 
  UpdateParticipantInfo, 
  UpdateParticipants 
} from './workoutAPI';
import { 
  InstructorInterface, 
  limitedWorkoutInterface, 
  participant, 
  workoutDetailsInterface, 
  WorkoutTypesInterface 
} from '../../app/consts';
import { localLogout } from '../authentication/authenticationSlice';

export interface WorkoutState {
  instructorList: InstructorInterface[];
  allWorkouts: workoutDetailsInterface[];
  limitedAllWorkouts: limitedWorkoutInterface[];
  dateFilter: string;
  activeFilter: string;
  allWorkoutTypes: WorkoutTypesInterface[];
  singleWorkout?: workoutDetailsInterface;
  editFlag: number;
  successFlag: number;
  showParticipantsModal: boolean;
  showEditModal: boolean;
  showManagerNotesModal: boolean;
  singleParticipantEmail?: string;
  sortBy: string;
  asc: boolean;
}

const initialState: WorkoutState = {
  instructorList: [],
  allWorkouts: [],
  limitedAllWorkouts: [],
  dateFilter: "future",
  activeFilter: "active",
  allWorkoutTypes: [],
  editFlag: 0,
  showParticipantsModal: false,
  showEditModal: false,
  showManagerNotesModal: false,
  successFlag: 0,
  sortBy: 'date',
  asc: true,
};

////////////////////////////////////////////// ASYNC THUNKS /////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////// NOT SECURED //////////////////////////////////////////////////////////////////////////////

export const GetSingleWorkoutAsync = createAsyncThunk(
  'Workout/GetSingleWorkout',
  async (id: string, { rejectWithValue }) => {
    try {
      return await GetSingleWorkout(id);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'שגיאה בטעינת האימון' });
    }
  }
);

export const updateParticipantsAsync = createAsyncThunk(
  'Workout/updateParticipants',
  async (
    workoutInfo: { id: string; participantInfo: participant },
    { rejectWithValue }
  ) => {
    try {
      return await UpdateParticipants(workoutInfo);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'אירעה שגיאה בשרת, נסה שוב מאוחר יותר' });
    }
  }
);

export const removeParticipantAsync = createAsyncThunk(
  'Workout/RemoveParticipant',
  async (
    workoutInfo: { workoutId: number; participantUid: string },
    { rejectWithValue }
  ) => {
    try {
      return await RemoveParticipant(workoutInfo);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'אירעה שגיאה בשרת, נסה שוב מאוחר יותר' });
    }
  }
);

export const SelfRemoveParticipantAsync = createAsyncThunk(
  'Workout/SelfRemoveParticipantAsync',
  async (
    data: { workoutId: number; uId: string; personalId: string; phoneNum: string; start: boolean },
    { rejectWithValue }
  ) => {
    try {
      return await selfRemoveParticipant(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'שגיאה בביטול ההרשמה' });
    }
  }
);

export const getParticipantEmailAsync = createAsyncThunk(
  'Workout/getParticipantEmail',
  async (data: { workoutId: string; uId: string }, { rejectWithValue }) => {
    try {
      return await getParticipantEmail(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'שגיאה בקבלת כתובת אימייל' });
    }
  }
);

export const getAllWorkoutsAsync = createAsyncThunk(
  'Workout/getAllWorkouts',
  async (_, { rejectWithValue }) => {
    try {
      return await getAllWorkouts();
    } catch (error: any) {
      return rejectWithValue(error.response?.data || { msg: 'שגיאה בטעינת אימונים' });
    }
  }
);

///////////////////////////////////////////// SECURED //////////////////////////////////////////////////////////////////////////////////

export const GetAllWorkoutsSecureAsync = createAsyncThunk(
  'Workout/GetAllWorkoutsSecured',
  async (data: { dateParam: string; statusParam: string }, { dispatch, rejectWithValue }) => {
    try {
      return await GetAllWorkoutsSecured(data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const GetSingleWorkoutSecuredAsync = createAsyncThunk(
  'Workout/GetSingleWorkoutSecured',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      return await GetSingleWorkoutSecured(id);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const AddWorkoutAsync = createAsyncThunk(
  'Workout/AddWorkout',
  async (workout: workoutDetailsInterface, { dispatch, rejectWithValue }) => {
    try {
      return await AddWorkout(workout);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const EditWorkoutAsync = createAsyncThunk(
  'Workout/EditWorkout',
  async (workoutInfo: { id: string; workout: workoutDetailsInterface }, { dispatch, rejectWithValue }) => {
    try {
      return await EditWorkout(workoutInfo);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const GetInstructorAsync = createAsyncThunk(
  'Workout/GetInstructor',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      return await GetInstructorList();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const AddInstructorAsync = createAsyncThunk(
  'Workout/AddInstructor',
  async (instructor: { fullName: string; phoneNumber: string }, { dispatch, rejectWithValue }) => {
    try {
      return await AddInstructor(instructor);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const GetWorkoutTypesAsync = createAsyncThunk(
  'Workout/GetWorkoutTypes',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      return await GetWorkoutsTypesList();
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const AddWorkoutTypeAsync = createAsyncThunk(
  'Workout/AddWorkoutType',
  async (workoutType: { name: string }, { dispatch, rejectWithValue }) => {
    try {
      return await AddWorkoutType(workoutType);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const UpdateParticipantInfoAsync = createAsyncThunk(
  'Workout/UpdateParticipantInfo',
  async (
    data: { workoutId: number; participantUniqueId: string; updatedData: participant },
    { dispatch, rejectWithValue }
  ) => {
    try {
      return await UpdateParticipantInfo(data);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const RemoveInstructorAsync = createAsyncThunk(
  'Workout/RemoveInstructor',
  async (id: number, { dispatch, rejectWithValue }) => {
    try {
      return await RemoveInstructor(id);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const RemoveWorkoutTypeAsync = createAsyncThunk(
  'Workout/RemoveWorkoutType',
  async (id: number, { dispatch, rejectWithValue }) => {
    try {
      return await RemoveWorkoutType(id);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        dispatch(localLogout("auto"));
      }
      return rejectWithValue(error?.response?.data);
    }
  }
);

/////////////////////////////////////////////// SORT LOGIC //////////////////////////////////////////////////////////

const mySortData = (state: WorkoutState, action: { payload: { by: string; type: string; asc?: boolean } }) => {
  state.sortBy = action.payload.by;
  state.asc = action.payload.asc ?? true;

  const data = action.payload.type === "complete" ? [...state.allWorkouts] : [...state.limitedAllWorkouts];
  let result: any[] = [];

  switch (state.sortBy) {
    case 'date':
      result = data.sort((a, b) => {
        const x = a.date;
        const y = b.date;
        if (x === y) {
          if (a.time > b.time) return state.asc ? -1 : 1;
          if (a.time < b.time) return state.asc ? 1 : -1;
          return 0;
        }
        if (x < y) return state.asc ? -1 : 1;
        if (x > y) return state.asc ? 1 : -1;
        return 0;
      });
      break;

    case 'type':
      result = data.sort((a, b) => {
        const x = a.type?.id;
        const y = b.type?.id;
        if (x < y) return state.asc ? -1 : 1;
        if (x > y) return state.asc ? 1 : -1;
        return 0;
      });
      break;

    case 'instructor':
      result = data.sort((a, b) => {
        const x = a.instructor?.id;
        const y = b.instructor?.id;
        if (x < y) return state.asc ? -1 : 1;
        if (x > y) return state.asc ? 1 : -1;
        return 0;
      });
      break;

    case 'location':
      result = data.sort((a, b) => {
        const x = a.location;
        const y = b.location;
        if (x < y) return state.asc ? -1 : 1;
        if (x > y) return state.asc ? 1 : -1;
        return 0;
      });
      break;

    case 'maximumParticipants':
      result = data.sort((a, b) => {
        return state.asc
          ? a.maximumParticipants - b.maximumParticipants
          : b.maximumParticipants - a.maximumParticipants;
      });
      break;

    default:
      result = data;
      break;
  }

  if (action.payload.type === "complete") {
    state.allWorkouts = result;
  } else if (action.payload.type === "limited") {
    state.limitedAllWorkouts = result;
  }
};

/////////////////////////////////////////////////////////// SLICE ///////////////////////////////////////////////////////////////////////////////////////

export const WorkoutSlice = createSlice({
  name: 'Workout',
  initialState,
  reducers: {
    setEditFlag: (state) => {
      state.editFlag++;
    },
    setSuccessFlag: (state) => {
      state.successFlag++;
    },
    sortData: mySortData,
    setShowManagerNotesModal: (state, action: PayloadAction<boolean>) => {
      state.showManagerNotesModal = action.payload;
    },
    setShowParticipantsModal: (state, action: PayloadAction<boolean>) => {
      state.showParticipantsModal = action.payload;
    },
    setShowEditModal: (state, action: PayloadAction<boolean>) => {
      state.showEditModal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetAllWorkoutsSecureAsync.fulfilled, (state, action) => {
        state.allWorkouts = action.payload;
        mySortData(state, { payload: { by: "date", type: "complete", asc: true } });
      })
      .addCase(GetSingleWorkoutAsync.fulfilled, (state, action) => {
        state.singleWorkout = action.payload;
      })
      .addCase(GetSingleWorkoutSecuredAsync.fulfilled, (state, action) => {
        state.singleWorkout = action.payload;
      })
      .addCase(GetInstructorAsync.fulfilled, (state, action) => {
        state.instructorList = action.payload;
      })
      .addCase(GetWorkoutTypesAsync.fulfilled, (state, action) => {
        state.allWorkoutTypes = action.payload;
      })
      .addCase(getParticipantEmailAsync.fulfilled, (state, action) => {
        state.singleParticipantEmail = action.payload?.email;
      })
      .addCase(getAllWorkoutsAsync.fulfilled, (state, action) => {
        state.limitedAllWorkouts = action.payload;
        mySortData(state, { payload: { by: "date", type: "limited", asc: true } });
      })
      .addCase(GetAllWorkoutsSecureAsync.rejected, (state) => {
        state.allWorkouts = [];
      })
      .addCase(GetSingleWorkoutSecuredAsync.rejected, (state) => {
        state.singleWorkout = undefined;
      })
      .addCase(GetInstructorAsync.rejected, (state) => {
        state.instructorList = [];
      })
      .addCase(GetWorkoutTypesAsync.rejected, (state) => {
        state.allWorkoutTypes = [];
      })
      .addCase(getParticipantEmailAsync.rejected, (state) => {
        state.singleParticipantEmail = "";
      });
  },
});

export const { 
  setEditFlag, 
  sortData, 
  setSuccessFlag, 
  setShowManagerNotesModal,
  setShowParticipantsModal,
  setShowEditModal
} = WorkoutSlice.actions;

export const selectWorkouts = (state: RootState) => state.Workout.allWorkouts;
export const selectLimitedWorkouts = (state: RootState) => state.Workout.limitedAllWorkouts;
export const selectSingleWorkout = (state: RootState) => state.Workout.singleWorkout;
export const selectInstructorList = (state: RootState) => state.Workout.instructorList;
export const selectWorkoutTypesList = (state: RootState) => state.Workout.allWorkoutTypes;
export const selectEditFlag = (state: RootState) => state.Workout.editFlag;
export const selectShowParticipantsModal = (state: RootState) => state.Workout.showParticipantsModal;
export const selectShowEditModal = (state: RootState) => state.Workout.showEditModal;
export const selectSingleParticipantEmail = (state: RootState) => state.Workout.singleParticipantEmail;
export const selectShowManagerNotesModal = (state: RootState) => state.Workout.showManagerNotesModal;
export const selectSortBy = (state: RootState) => state.Workout.sortBy;
export const selectAsc = (state: RootState) => state.Workout.asc;

export default WorkoutSlice.reducer;